import { useState } from 'react';
import { Link } from 'react-router-dom';
import ChatWidget from '../../components/feature/ChatWidget';

interface Vehicle {
  id: string;
  year: string;
  make: string;
  model: string;
  vin: string;
  mileage: string;
  primaryUse: string;
  annualMileage: string;
}

interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  licenseNumber: string;
  licenseState: string;
  licenseExpiration: string;
  relationship: string;
}

export default function AutoQuote() {
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      maritalStatus: 'single',
      licenseNumber: '',
      licenseState: '',
      licenseExpiration: ''
    },
    spouseInfo: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      licenseNumber: '',
      licenseState: '',
      licenseExpiration: ''
    },
    vehicleInfo: {
      year: '',
      make: '',
      model: '',
      vin: '',
      mileage: '',
      primaryUse: 'commuting',
      annualMileage: ''
    },
    coverage: {
      liability: '100/300/100',
      collision: true,
      comprehensive: true,
      uninsured: true,
      pip: false
    },
    drivingHistory: {
      yearsLicensed: '',
      accidents: '0',
      violations: '0',
      previousInsurance: true
    }
  });

  const [additionalDrivers, setAdditionalDrivers] = useState<Driver[]>([]);
  const [additionalVehicles, setAdditionalVehicles] = useState<Vehicle[]>([]);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);

  const [newDriver, setNewDriver] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    licenseNumber: '',
    licenseState: '',
    licenseExpiration: '',
    relationship: ''
  });

  const [newVehicle, setNewVehicle] = useState({
    year: '',
    make: '',
    model: '',
    vin: '',
    mileage: '',
    primaryUse: 'commuting',
    annualMileage: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (section: string, field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
  };

  const addDriver = () => {
    const driver: Driver = {
      id: Date.now().toString(),
      ...newDriver
    };
    setAdditionalDrivers([...additionalDrivers, driver]);
    setNewDriver({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      licenseNumber: '',
      licenseState: '',
      licenseExpiration: '',
      relationship: ''
    });
    setShowAddDriver(false);
  };

  const removeDriver = (id: string) => {
    setAdditionalDrivers(additionalDrivers.filter(driver => driver.id !== id));
  };

  const addVehicle = () => {
    const vehicle: Vehicle = {
      id: Date.now().toString(),
      ...newVehicle
    };
    setAdditionalVehicles([...additionalVehicles, vehicle]);
    setNewVehicle({
      year: '',
      make: '',
      model: '',
      vin: '',
      mileage: '',
      primaryUse: 'commuting',
      annualMileage: ''
    });
    setShowAddVehicle(false);
  };

  const removeVehicle = (id: string) => {
    setAdditionalVehicles(additionalVehicles.filter(vehicle => vehicle.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { number: 1, title: 'Personal Information', icon: 'ri-user-line' },
    { number: 2, title: 'Vehicle Details', icon: 'ri-car-line' },
    { number: 3, title: 'Coverage Options', icon: 'ri-shield-check-line' },
    { number: 4, title: 'Driving History', icon: 'ri-road-map-line' }
  ];

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Home Button */}
        <Link 
          to="/" 
          className="fixed top-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-50 cursor-pointer"
        >
          <i className="ri-home-line text-xl"></i>
        </Link>

        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center max-w-2xl w-full">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Quote Request Submitted!</h1>
            <p className="text-xl text-gray-600 mb-8">
              Thank you for choosing Dr. Peterson's Insurance. We've received your auto quote request and one of our insurance specialists will contact you within 24 hours with your personalized quote.
            </p>
            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-left space-y-2 text-blue-800">
                <li className="flex items-center">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  We'll review your information and run quotes with multiple carriers
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  One of our specialists will call you within 24 hours
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  We'll present you with the best coverage options and rates
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <Link 
                to="/" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Return to Home
              </Link>
              <p className="text-gray-500">
                Questions? Call us at <a href="tel:555-123-4567" className="text-blue-600 hover:underline">(555) 123-4567</a>
              </p>
            </div>
          </div>
        </div>

        <ChatWidget 
          apiEndpoint="https://voice.theinsurancedoctors.com/readdy-chat"
          apiKey=""
          agentName="Dr. Peterson's AI Assistant"
          welcomeMessage="Hi! I'm Dr. Peterson's AI assistant. How can I help today?"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Home Button */}
      <Link 
        to="/" 
        className="fixed top-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-50 cursor-pointer"
      >
        <i className="ri-home-line text-xl"></i>
      </Link>

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Get Your Auto Insurance Quote</h1>
            <p className="text-lg text-gray-600">Fast, easy, and personalized coverage options</p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-12">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                currentStep >= step.number 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              } transition-all duration-300`}>
                <i className={`${step.icon} text-lg`}></i>
              </div>
              <div className="ml-3 hidden sm:block">
                <p className={`text-sm font-medium ${
                  currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  Step {step.number}
                </p>
                <p className={`text-xs ${
                  currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 ml-6 ${
                  currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                } transition-all duration-300`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Personal Information</h2>
              
              {/* Primary Driver Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Primary Driver Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.personalInfo.firstName}
                      onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.personalInfo.lastName}
                      onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.personalInfo.email}
                      onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.personalInfo.phone}
                      onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      required
                      value={formData.personalInfo.dateOfBirth}
                      onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      required
                      value={formData.personalInfo.zipCode}
                      onChange={(e) => handleInputChange('personalInfo', 'zipCode', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="12345"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status *</label>
                    <select
                      required
                      value={formData.personalInfo.maritalStatus}
                      onChange={(e) => handleInputChange('personalInfo', 'maritalStatus', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Driver License Number *</label>
                    <input
                      type="text"
                      required
                      value={formData.personalInfo.licenseNumber}
                      onChange={(e) => handleInputChange('personalInfo', 'licenseNumber', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="License number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">License State *</label>
                    <select
                      required
                      value={formData.personalInfo.licenseState}
                      onChange={(e) => handleInputChange('personalInfo', 'licenseState', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select state</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">License Expiration Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.personalInfo.licenseExpiration}
                      onChange={(e) => handleInputChange('personalInfo', 'licenseExpiration', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Spouse Information */}
              {formData.personalInfo.maritalStatus === 'married' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Spouse Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spouse First Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.spouseInfo.firstName}
                        onChange={(e) => handleInputChange('spouseInfo', 'firstName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Spouse's first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Last Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.spouseInfo.lastName}
                        onChange={(e) => handleInputChange('spouseInfo', 'lastName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Spouse's last name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Date of Birth *</label>
                      <input
                        type="date"
                        required
                        value={formData.spouseInfo.dateOfBirth}
                        onChange={(e) => handleInputChange('spouseInfo', 'dateOfBirth', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spouse License Number *</label>
                      <input
                        type="text"
                        required
                        value={formData.spouseInfo.licenseNumber}
                        onChange={(e) => handleInputChange('spouseInfo', 'licenseNumber', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Spouse's license number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spouse License State *</label>
                      <select
                        required
                        value={formData.spouseInfo.licenseState}
                        onChange={(e) => handleInputChange('spouseInfo', 'licenseState', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select state</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Spouse License Expiration *</label>
                      <input
                        type="date"
                        required
                        value={formData.spouseInfo.licenseExpiration}
                        onChange={(e) => handleInputChange('spouseInfo', 'licenseExpiration', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Drivers */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Additional Drivers</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddDriver(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add Driver
                  </button>
                </div>
                
                {additionalDrivers.length > 0 && (
                  <div className="space-y-4">
                    {additionalDrivers.map((driver) => (
                      <div key={driver.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div>
                          <h4 className="font-semibold text-gray-900">{driver.firstName} {driver.lastName}</h4>
                          <p className="text-sm text-gray-600">
                            {driver.relationship} • Born {new Date(driver.dateOfBirth).toLocaleDateString()} • License: {driver.licenseNumber}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDriver(driver.id)}
                          className="text-red-600 hover:text-red-800 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Information */}
          {currentStep === 2 && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Vehicle Information</h2>
              
              {/* Primary Vehicle */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Primary Vehicle</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                    <input
                      type="number"
                      required
                      min="1990"
                      max="2025"
                      value={formData.vehicleInfo.year}
                      onChange={(e) => handleInputChange('vehicleInfo', 'year', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="2020"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                    <input
                      type="text"
                      required
                      value={formData.vehicleInfo.make}
                      onChange={(e) => handleInputChange('vehicleInfo', 'make', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Toyota"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                    <input
                      type="text"
                      required
                      value={formData.vehicleInfo.model}
                      onChange={(e) => handleInputChange('vehicleInfo', 'model', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Camry"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">VIN Number *</label>
                    <input
                      type="text"
                      required
                      value={formData.vehicleInfo.vin}
                      onChange={(e) => handleInputChange('vehicleInfo', 'vin', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="17-digit VIN number"
                      maxLength={17}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Mileage *</label>
                    <input
                      type="number"
                      required
                      value={formData.vehicleInfo.mileage}
                      onChange={(e) => handleInputChange('vehicleInfo', 'mileage', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="45000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Use *</label>
                    <select
                      required
                      value={formData.vehicleInfo.primaryUse}
                      onChange={(e) => handleInputChange('vehicleInfo', 'primaryUse', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="commuting">Commuting to Work</option>
                      <option value="pleasure">Pleasure/Personal</option>
                      <option value="business">Business Use</option>
                      <option value="farm">Farm Use</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Annual Mileage *</label>
                    <input
                      type="number"
                      required
                      value={formData.vehicleInfo.annualMileage}
                      onChange={(e) => handleInputChange('vehicleInfo', 'annualMileage', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="12000"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Vehicles */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Additional Vehicles</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddVehicle(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add Vehicle
                  </button>
                </div>
                
                {additionalVehicles.length > 0 && (
                  <div className="space-y-4">
                    {additionalVehicles.map((vehicle) => (
                      <div key={vehicle.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div>
                          <h4 className="font-semibold text-gray-900">{vehicle.year} {vehicle.make} {vehicle.model}</h4>
                          <p className="text-sm text-gray-600">
                            VIN: {vehicle.vin} • Mileage: {vehicle.mileage} • Use: {vehicle.primaryUse}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeVehicle(vehicle.id)}
                          className="text-red-600 hover:text-red-800 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Coverage Options */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Coverage Options</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Liability Coverage *</label>
                  <div className="relative">
                    <select
                      required
                      value={formData.coverage.liability}
                      onChange={(e) => handleInputChange('coverage', 'liability', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="50/100/50">$50K/$100K/$50K (Minimum)</option>
                      <option value="100/300/100">$100K/$300K/$100K (Recommended)</option>
                      <option value="250/500/250">$250K/$500K/$250K (High)</option>
                      <option value="500/1000/500">$500K/$1M/$500K (Maximum)</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Collision Coverage</h4>
                      <p className="text-sm text-gray-600">Covers damage to your vehicle</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.coverage.collision}
                        onChange={(e) => handleInputChange('coverage', 'collision', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Comprehensive Coverage</h4>
                      <p className="text-sm text-gray-600">Covers theft, vandalism, weather</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.coverage.comprehensive}
                        onChange={(e) => handleInputChange('coverage', 'comprehensive', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Uninsured Motorist</h4>
                      <p className="text-sm text-gray-600">Protection from uninsured drivers</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.coverage.uninsured}
                        onChange={(e) => handleInputChange('coverage', 'uninsured', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Personal Injury Protection</h4>
                      <p className="text-sm text-gray-600">Medical expenses coverage</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.coverage.pip}
                        onChange={(e) => handleInputChange('coverage', 'pip', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Driving History */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Driving History</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years Licensed *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="70"
                    value={formData.drivingHistory.yearsLicensed}
                    onChange={(e) => handleInputChange('drivingHistory', 'yearsLicensed', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accidents (Last 5 Years) *</label>
                  <div className="relative">
                    <select
                      required
                      value={formData.drivingHistory.accidents}
                      onChange={(e) => handleInputChange('drivingHistory', 'accidents', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="0">0 Accidents</option>
                      <option value="1">1 Accident</option>
                      <option value="2">2 Accidents</option>
                      <option value="3+">3+ Accidents</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Violations (Last 5 Years) *</label>
                  <div className="relative">
                    <select
                      required
                      value={formData.drivingHistory.violations}
                      onChange={(e) => handleInputChange('drivingHistory', 'violations', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="0">0 Violations</option>
                      <option value="1">1 Violation</option>
                      <option value="2">2 Violations</option>
                      <option value="3+">3+ Violations</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">Currently Insured</h4>
                    <p className="text-sm text-gray-600">Do you have current auto insurance?</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.drivingHistory.previousInsurance}
                      onChange={(e) => handleInputChange('drivingHistory', 'previousInsurance', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-8 border-t">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>
            
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={nextStep}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Next Step
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white hover:scale-105'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Get My Quote'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Add Driver Modal */}
      {showAddDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Additional Driver</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={newDriver.firstName}
                    onChange={(e) => setNewDriver({ ...newDriver, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={newDriver.lastName}
                    onChange={(e) => setNewDriver({ ...newDriver, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    value={newDriver.dateOfBirth}
                    onChange={(e) => setNewDriver({ ...newDriver, dateOfBirth: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                  <select
                    value={newDriver.relationship}
                    onChange={(e) => setNewDriver({ ...newDriver, relationship: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                  >
                    <option value="">Select relationship</option>
                    <option value="spouse">Spouse</option>
                    <option value="daughter">Daughter</option>
                    <option value="son">Son</option>
                    <option value="parent">Parent</option>
                    <option value="other-relative">Other Relative</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">License Number</label>
                  <input
                    type="text"
                    value={newDriver.licenseNumber}
                    onChange={(e) => setNewDriver({ ...newDriver, licenseNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="License number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">License State</label>
                  <select
                    value={newDriver.licenseState}
                    onChange={(e) => setNewDriver({ ...newDriver, licenseState: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                  >
                    <option value="">Select state</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">License Expiration Date</label>
                  <input
                    type="date"
                    value={newDriver.licenseExpiration}
                    onChange={(e) => setNewDriver({ ...newDriver, licenseExpiration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddDriver(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={addDriver}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Add Driver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Additional Vehicle</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <input
                    type="number"
                    min="1990"
                    max="2025"
                    value={newVehicle.year}
                    onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="2020"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                  <input
                    type="text"
                    value={newVehicle.make}
                    onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Toyota"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                  <input
                    type="text"
                    value={newVehicle.model}
                    onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Camry"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">VIN Number</label>
                  <input
                    type="text"
                    value={newVehicle.vin}
                    onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="17-digit VIN number"
                    maxLength={17}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Mileage</label>
                  <input
                    type="number"
                    value={newVehicle.mileage}
                    onChange={(e) => setNewVehicle({ ...newVehicle, mileage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="45000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Use</label>
                  <select
                    value={newVehicle.primaryUse}
                    onChange={(e) => setNewVehicle({ ...newVehicle, primaryUse: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                  >
                    <option value="commuting">Commuting to Work</option>
                    <option value="pleasure">Pleasure/Personal</option>
                    <option value="business">Business Use</option>
                    <option value="farm">Farm Use</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Mileage</label>
                  <input
                    type="number"
                    value={newVehicle.annualMileage}
                    onChange={(e) => setNewVehicle({ ...newVehicle, annualMileage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="12000"
                  />
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddVehicle(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={addVehicle}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Add Vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatWidget 
        apiEndpoint="https://voice.theinsurancedoctors.com/readdy-chat"
        apiKey=""
        agentName="Dr. Peterson's AI Assistant"
        welcomeMessage="Hi! I'm Dr. Peterson's AI assistant. How can I help today?"
      />
    </div>
  );
}
